import type { ReactNode } from 'react';
import { Pin, PinOff, Palette, ImageIcon } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@clarix/ui';
import { usePermission, P } from '@clarix/rbac';
import type { ProjectListItem } from '@clarix/shared-types';

interface SidebarProjectContextMenuProps {
  project: ProjectListItem;
  isPinned: boolean;
  onTogglePin: (projectId: string) => void;
  onChangeIcon: (project: ProjectListItem) => void;
  onChangeColor: (project: ProjectListItem) => void;
  children: ReactNode;
}

export function SidebarProjectContextMenu({
  project,
  isPinned,
  onTogglePin,
  onChangeIcon,
  onChangeColor,
  children,
}: SidebarProjectContextMenuProps) {
  const { canForProject } = usePermission();
  const canManageSettings = canForProject(P.SETTINGS_PROJECT_CONFIG, project.id);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuItem
          onClick={() => onTogglePin(project.id)}
          className="cursor-pointer gap-2 text-lg"
        >
          {isPinned ? (
            <PinOff className="h-3.5 w-3.5" strokeWidth={1.5} />
          ) : (
            <Pin className="h-3.5 w-3.5" strokeWidth={1.5} />
          )}
          {isPinned ? 'Unpin Project' : 'Pin to Top'}
        </ContextMenuItem>
        {canManageSettings && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => onChangeIcon(project)}
              className="cursor-pointer gap-2 text-lg"
            >
              <ImageIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
              Change Icon
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => onChangeColor(project)}
              className="cursor-pointer gap-2 text-lg"
            >
              <Palette className="h-3.5 w-3.5" strokeWidth={1.5} />
              Change Color
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
